import { createContext, useContext } from 'react';

// One place that knows whether anything currently on screen has unsaved edits.
//
// useUnsavedChangesPrompt already guards route changes, but switching the org/brand/location scope is NOT a
// route change — it only updates AppContext — so react-router's blocker never sees it and a half-finished
// edit was discarded without a word. The scope selector needs to ask first, and to do that it needs to know
// that something, somewhere, is dirty.
//
// Pages do not talk to this directly: useUnsavedChangesPrompt publishes on their behalf, so every screen that
// already guards its route changes is covered here too.

export type UnsavedChangesContextValue = {
    isDirty: boolean;
    setDirty: (key: string, dirty: boolean) => void;
    // Abandon whatever is pending, because the user has already been asked and said Leave.
    //
    // Answering the scope prompt used to change the scope and nothing else, so a brand or location editor
    // stayed open with the edits the user had just agreed to discard — still showing the old scope's record.
    // The prompt asked a question and then ignored the answer.
    //
    // Each guarded screen says how to leave itself (in practice: navigate back to its list), because the
    // scope selector has no business knowing the route of every editor in the portal.
    discard: () => void;
    // Pass undefined to withdraw. A screen with nothing to do on discard simply never registers.
    registerDiscard: (key: string, handler: (() => void) | undefined) => void;
    // True from the moment Leave is answered until the last dirty screen has gone. The router guard stands
    // down while it holds, so the navigation a discard triggers cannot re-ask a question already answered.
    isDiscarding: () => boolean;
};

export const UnsavedChangesContext = createContext<UnsavedChangesContextValue | undefined>(undefined);

// Returns false when no provider is mounted, so a component used outside the app shell behaves as though
// nothing is pending rather than throwing.
export function useUnsavedChanges(): UnsavedChangesContextValue {
    return useContext(UnsavedChangesContext) ?? {
        isDirty: false,
        setDirty: () => {},
        discard: () => {},
        registerDiscard: () => {},
        isDiscarding: () => false,
    };
}
